const SOLANA_CHAIN = "solana:";

export interface StandardAccount {
  readonly address: string;
  readonly features?: readonly string[];
}

interface ConnectFeature {
  connect(input?: { silent?: boolean }): Promise<{ accounts: readonly StandardAccount[] }>;
}

interface SignTransactionFeature {
  signTransaction(
    ...inputs: { account: StandardAccount; transaction: Uint8Array; chain?: string }[]
  ): Promise<{ signedTransaction: Uint8Array }[]>;
}

interface SignMessageFeature {
  signMessage(
    ...inputs: { account: StandardAccount; message: Uint8Array }[]
  ): Promise<{ signature: Uint8Array }[]>;
}

export interface StandardWallet {
  readonly name: string;
  readonly icon?: string;
  readonly chains: readonly string[];
  readonly features: Record<string, unknown>;
  readonly accounts: readonly StandardAccount[];
}

export interface WalletHandle {
  readonly name: string;
  readonly icon: string | null;

  readonly signsBatches: boolean;
  connect(): Promise<string>;
  signMessage(message: Uint8Array): Promise<Uint8Array>;

  signTransactions(transactions: readonly Uint8Array[]): Promise<Uint8Array[]>;
}

const solanaFeatures = (wallet: StandardWallet): string[] => {
  try {
    return Object.keys(wallet.features ?? {}).filter((name) => name.startsWith(SOLANA_CHAIN));
  } catch {
    return [];
  }
};

const solanaChains = (wallet: StandardWallet): string[] => {
  try {
    return [...(wallet.chains ?? [])].filter((chain) => chain.startsWith(SOLANA_CHAIN));
  } catch {
    return [];
  }
};

const speaksSolana = (wallet: StandardWallet): boolean =>
  solanaChains(wallet).length > 0 || solanaFeatures(wallet).length > 0;

export const describeRegistered = (): string[] =>
  [...found.values()].map(
    (wallet) =>
      `${wallet.name}[chains=${solanaChains(wallet).length || "none"} features=${solanaFeatures(wallet).join("|") || "none"} ${speaksSolana(wallet) ? "OFFERED" : "DROPPED"}]`,
  );

export const handleFor = (wallet: StandardWallet): WalletHandle => {
  let account: StandardAccount | null = null;

  const feature = <T>(name: string): T | null => (wallet.features[name] as T | undefined) ?? null;

  const held = (): StandardAccount => {
    if (account === null) throw new Error(`${wallet.name} is not connected`);
    return account;
  };

  return {
    name: wallet.name,
    icon: wallet.icon ?? null,
    signsBatches: feature("solana:signTransaction") !== null,

    async connect(): Promise<string> {
      const connecting = feature<ConnectFeature>("standard:connect");
      if (connecting === null) throw new Error(`${wallet.name} does not support connecting`);
      const { accounts } = await connecting.connect();
      const first = accounts[0] ?? wallet.accounts[0];
      if (first === undefined) throw new Error(`${wallet.name} returned no account`);
      account = first;
      return first.address;
    },

    async signMessage(message: Uint8Array): Promise<Uint8Array> {
      const signing = feature<SignMessageFeature>("solana:signMessage");
      if (signing === null) throw new Error(`${wallet.name} cannot sign messages`);
      const [signed] = await signing.signMessage({ account: held(), message });
      if (signed === undefined) throw new Error(`${wallet.name} returned no signature`);
      return signed.signature;
    },

    async signTransactions(transactions: readonly Uint8Array[]): Promise<Uint8Array[]> {
      const signing = feature<SignTransactionFeature>("solana:signTransaction");
      if (signing === null) throw new Error(`${wallet.name} cannot sign transactions`);
      const signed = await signing.signTransaction(
        ...transactions.map((transaction) => ({ account: held(), transaction })),
      );
      if (signed.length !== transactions.length) {
        throw new Error(
          `${wallet.name} returned ${signed.length} signed transactions for ${transactions.length}`,
        );
      }
      return signed.map((one) => one.signedTransaction);
    },
  };
};

interface LegacyProvider {
  connect(): Promise<{ publicKey: { toBase58(): string } }>;
  signMessage(
    message: Uint8Array,
    encoding?: string,
  ): Promise<{ signature: Uint8Array } | Uint8Array>;
  signTransaction<T>(tx: T): Promise<T>;
  signAllTransactions?<T>(txs: T[]): Promise<T[]>;
}

export const legacyHandle = (
  provider: LegacyProvider,
  name: string,
  codec: { decode(raw: Uint8Array): unknown; encode(tx: unknown): Uint8Array },
): WalletHandle => ({
  name,
  icon: null,
  signsBatches: typeof provider.signAllTransactions === "function",

  async connect(): Promise<string> {
    const { publicKey } = await provider.connect();
    return publicKey.toBase58();
  },

  async signMessage(message: Uint8Array): Promise<Uint8Array> {
    const answered = await provider.signMessage(message, "utf8");
    return answered instanceof Uint8Array ? answered : answered.signature;
  },

  async signTransactions(transactions: readonly Uint8Array[]): Promise<Uint8Array[]> {
    const decoded = transactions.map((raw) => codec.decode(raw));
    const signAll = provider.signAllTransactions?.bind(provider);
    const signed =
      signAll === undefined || decoded.length === 1
        ? [await provider.signTransaction(decoded[0] as object)]
        : await signAll(decoded);
    return signed.map((one) => codec.encode(one));
  },
});

interface RegistryApi {
  register(...wallets: StandardWallet[]): () => void;
  get(): readonly StandardWallet[];
  on(): () => void;
}

const found = new Map<string, StandardWallet>();
let listening = false;

const api: RegistryApi = {
  register: (...wallets) => {
    for (const wallet of wallets) found.set(wallet.name, wallet);
    return () => {};
  },
  get: () => [...found.values()],
  on: () => () => {},
};

let heard = 0;
let refused = 0;

const announce = (event: Event): void => {
  heard += 1;
  const detail = (event as CustomEvent<(api: RegistryApi) => void>).detail;
  if (typeof detail !== "function") {
    refused += 1;
    return;
  }
  try {
    detail(api);
  } catch {
    refused += 1;
  }
};

export const registryState = (): { heard: number; refused: number; registered: string[] } => ({
  heard,
  refused,
  registered: [...found.values()].map((wallet) => wallet.name),
});

export const discover = (): WalletHandle[] => {
  if (!listening) {
    listening = true;
    window.addEventListener("wallet-standard:register-wallet", announce);
  }

  window.dispatchEvent(new CustomEvent("wallet-standard:app-ready", { detail: api }));
  return [...found.values()].filter(speaksSolana).map(handleFor);
};

export const registeredNames = (): string[] => [...found.values()].map((wallet) => wallet.name);
